import {
  Fragment,
  type CSSProperties,
  type KeyboardEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { UseFormReturnType } from '@mantine/form';
import type { EmissionReportCellValue, EmissionReportData } from '../../types/entities';
import { EMISSION_INPUT_PROPS, handleDecimalKey } from '../form/numberFieldPresets.js';
import { NO_REMOUNT, ValidatedNumberInput } from '../form/validatedFields.js';
import { importableEmissionGroups } from './importableEmissionFields.js';
import { applyCellEdit, cellText, cellTitle, isChanged } from './matrixEdits.js';
import { firstCell, nextCell, normalizeCell, type MatrixCellRef, type MatrixGrid } from './matrixNavigation.js';
import classes from './EmissionReportMatrix.module.css';

/**
 * Die Vorschau-Matrix (Plan 35-12, Sketch 020): Merkmale als Zeilen, Messungen als Spalten, mit
 * dem Block "Bezugsgroessen der Messung" darueber. Jede Zelle wird auf Klick zum Eingabefeld;
 * Feld leeren heisst "dieser Wert wird nicht uebernommen". Eine ganze Messung wird ueber den
 * Spaltenkopf als geloescht MARKIERT (`excluded: true`, Punkt 4/WR-15/WR-12, 35.1-04) statt
 * entfernt -- widerrufbar, bis "Uebernehmen" gedrueckt ist (der Widerruf selbst lebt in der
 * Fusszeile der aufrufenden Seite, siehe `EmissionReportDetailPage.tsx`). Ausgeschlossene Spalten
 * werden ausgeblendet (`visibleColumns`), bleiben aber im Formularwert stehen; der Spaltenkopf
 * zeigt deshalb weiterhin die URSPRUENGLICHE Nummer der Messung, statt neu durchzunummerieren --
 * eine bewusste Abweichung von Sketch 020, weil sich Protokolleintraege und Rueckgaengig-Text auf
 * genau diese Nummer beziehen.
 *
 * `form` ist direkt an die Struktur von `resultData` gebunden (Modus `controlled`, gesetzt vom
 * Aufrufer) -- kein Zwischenzustand, kein `useState` fuer Zellwerte. Was hier lokalen State
 * bekommt, ist AUSSCHLIESSLICH die Frage "welche Zelle ist gerade im Bearbeitungsmodus" -- eine
 * UI-Frage, kein Formularfeld.
 */

/**
 * Breite der Merkmalsspalte (Punkt 13): im Browser gemessen (Playwright, `getBoundingClientRect`).
 * Breiteste Beschriftung „Bezugssauerstoff (O₂)“ = 123,9px Textbreite, „Werte bezogen auf“ =
 * 108,1px, je ~9px Zellenpolsterung auf beiden Seiten -- 160px lassen rund 18px Reserve fuer
 * abweichende Schriftbreiten auf anderen Systemen.
 */
export const MATRIX_LABEL_COLUMN_WIDTH_PX = 160;

function getPath(values: EmissionReportData, path: string): unknown {
  return path.split('.').reduce<unknown>((acc, key) => {
    if (acc === null || acc === undefined) return undefined;
    return (acc as Record<string, unknown>)[key];
  }, values as unknown as Record<string, unknown>);
}

/** `2026-03-10` -> `10.03.2026`, rein ueber Zeichenketten (dieselbe Falle wie anderswo im
 *  Import: `new Date(iso)` zeigt in Zonen mit negativem Offset den Vortag). */
function formatIsoDate(iso: string): string {
  const [year, month, day] = iso.slice(0, 10).split('-');
  return day && month && year ? `${day}.${month}.${year}` : iso;
}

/** `data-cell`-Wert einer Matrixzelle, zugleich Schluessel in `cellRefs` (Punkt 9). */
function cellKey(ref: MatrixCellRef): string {
  return `${ref.rowKey}|${ref.position}`;
}

/** Naechster Vorfahre mit eigenem senkrechtem Scrollen -- der Scrollbereich der Detailseite. */
function findScrollContainer(element: HTMLElement): HTMLElement | null {
  let node = element.parentElement;
  while (node) {
    const overflowY = window.getComputedStyle(node).overflowY;
    if (overflowY === 'auto' || overflowY === 'scroll') {
      return node;
    }
    node = node.parentElement;
  }
  return null;
}

/**
 * Luecke aus Punkt 9 (in der Durchsprache nicht benannt, gleiche Ursache wie bei Punkt 13): Der
 * Kopf klebt ueber zwei Ebenen (`.mxHead` + `thead`, siehe `EmissionReportMatrix.module.css`).
 * `scrollIntoView({ block: 'nearest' })` kennt diese Ueberdeckung nicht -- eine per Pfeil-nach-
 * oben fokussierte Zelle kann trotzdem unter dem klebenden Kopf verschwinden. Deshalb wird nach
 * jedem Pfeilschritt die tatsaechliche (gerenderte) Unterkante des klebenden Kopfes gegen die
 * Zelle geprueft und der Scrollbereich der Detailseite manuell um die Ueberdeckung nachgezogen.
 *
 * WICHTIG: `position: sticky` sitzt in `EmissionReportMatrix.module.css` an den `th`-Zellen
 * selbst (`.mx thead th { position: sticky; top: var(--mx-head-height); }`), NICHT am `thead`.
 * Das `thead`-Element bleibt `position: static` und damit an seiner Flussposition stehen, waehrend
 * die `th` beim Scrollen sichtbar kleben -- `thead.getBoundingClientRect().bottom` haette daher
 * eine LOSGELOESTE, zu kleine (bzw. bei starkem Scrollen sogar negative) Unterkante geliefert
 * (Browserbefund Task 3: `thead.bottom` 98,3 gegen die tatsaechliche `th`-Unterkante 274,7 bei
 * `scrollTop=400`, ein Sprung nach ArrowUp landete dadurch 34px unter dem klebenden Kopf). Deshalb
 * wird ueber die tatsaechlich gerenderten `th`-Rects gemessen, nicht ueber ihren Container.
 *
 * Entscheidung gegen `scrollPaddingTop` an der Detailseite (die im Plan genannte Alternative,
 * analog zu `scrollPaddingLeft` aus Plan 05): die Kopfhoehe ist dort nur ein Startwert
 * (`--mx-head-height: 45px`), die tatsaechliche Gesamthoehe der zwei sticky-Ebenen ist erst zur
 * Laufzeit bekannt (Schriftgroesse/Zoom/OS-Rendering). Eine Laufzeitmessung bleibt robust gegen
 * genau die Aenderungen, die IN-08 ueberhaupt erst verhindern sollte.
 */
function stickyHeaderOverlap(cellElement: HTMLElement, theadElement: HTMLTableSectionElement): number {
  const headerCells = theadElement.querySelectorAll('th');
  const headerBottom = Math.max(
    0,
    ...Array.from(headerCells, (th) => th.getBoundingClientRect().bottom)
  );
  return headerBottom - cellElement.getBoundingClientRect().top;
}

function correctForStickyHeaderOverlap(cellElement: HTMLElement): void {
  const theadElement = cellElement.closest('table')?.querySelector('thead');
  const scrollContainer = findScrollContainer(cellElement);
  if (!theadElement || !scrollContainer) {
    return;
  }
  const overlap = stickyHeaderOverlap(cellElement, theadElement);
  if (overlap > 0) {
    scrollContainer.scrollTop -= overlap;
  }
}

type EditTarget =
  | { kind: 'value'; measurementIndex: number; fieldKey: string }
;

function editKey(target: EditTarget): string {
  return target.kind === 'value'
    ? `value:${target.measurementIndex}:${target.fieldKey}`
    : `${target.kind}:${target.measurementIndex}`;
}

export interface EmissionReportMatrixProps {
  /** An `resultData` gebundenes Formular (Modus `controlled`), vom Aufrufer erzeugt. */
  form: UseFormReturnType<EmissionReportData>;
  /** Der unveraenderte Originalstand -- einzige Quelle der Aenderungsmarkierung (D-05). */
  sourceData: EmissionReportData;
  /** Nach dem Uebernehmen: reiner Text, kein Eingabefeld, kein Loeschen-Knopf. */
  readOnly: boolean;
  onDeleteMeasurement: (index: number) => void;
}

export function EmissionReportMatrix({
  form,
  sourceData,
  readOnly,
  onDeleteMeasurement,
}: EmissionReportMatrixProps) {
  const [editing, setEditing] = useState<EditTarget | null>(null);
  // Schnappschuss der Zelle VOR der aktuellen Bearbeitung -- fuer `applyCellEdit` (setCell
  // braucht den Vor-Edit-Stand) und fuer den Widerruf per Escape. Kein Formularfeld, sondern
  // reine Zwischenablage fuer die laufende Bearbeitung.
  const snapshotRef = useRef<{ key: string; cell: EmissionReportCellValue } | null>(null);
  // Punkt 7 (WR-14): Die Matrixzeilen kommen aus den DATEN, nicht mehr aus einer eigenen
  // Ausschlussliste -- `sourceData` ist der eingefrorene Beleg (D-06) und traegt bei jeder
  // Messung dieselben Schluessel (`buildDocument()`), die erste Messung genuegt deshalb als
  // Quelle. Beschriftung, Einheit und Gruppenreihenfolge liefert weiterhin das Formularverzeichnis.
  const rowGroups = useMemo(
    () => importableEmissionGroups(Object.keys(sourceData.measurements[0]?.values ?? {})),
    [sourceData]
  );

  const values = form.getValues();
  const measurements = values.measurements;
  // Punkt 4 (WR-15/WR-12): Loeschen markiert eine Messung nur noch (`excluded: true`), das Array
  // behaelt Laenge und Reihenfolge. Die Matrix blendet ausgeschlossene Spalten aus, adressiert
  // Pfade/Bearbeitungszustand aber weiterhin ueber `position` (den ORIGINALEN Index im Array,
  // nicht den Index in dieser gefilterten Liste) -- Positionsbezuege bleiben so stabil.
  const visibleColumns = measurements
    .map((measurement, position) => ({ measurement, position }))
    .filter(({ measurement }) => measurement.excluded !== true);
  const columnCount = visibleColumns.length + 2; // Merkmalsspalte + Einheitenspalte

  // Punkt 9 (WR-13): Tastaturnavigation, nur in der offenen Vorschau. Zeilenschluessel in
  // Anzeigereihenfolge (siehe `matrixNavigation.ts`), `excluded` ueber ALLE Positionen (nicht nur
  // `visibleColumns`), weil `position` immer der ORIGINALE Index im Array ist.
  const rowKeys = useMemo(() => {
    const keys: string[] = [];
    for (const group of rowGroups) {
      for (const field of group.fields) {
        keys.push(`value:${field.key}`);
      }
    }
    return keys;
  }, [rowGroups]);
  const excludedFlags = measurements.map((measurement) => measurement.excluded === true);
  const grid: MatrixGrid = { rowKeys, excluded: excludedFlags };
  // Fasst den tatsaechlichen Inhalt des Rasters zusammen (nicht nur die Objektidentitaet, die bei
  // jedem Render neu ist) -- der Abgleich unten soll nur laufen, wenn sich Zeilen oder
  // Ausschluesse WIRKLICH geaendert haben.
  const gridKey = `${rowKeys.join(',')}|${excludedFlags.map((flag) => (flag ? '1' : '0')).join('')}`;

  const [focusCell, setFocusCell] = useState<MatrixCellRef | null>(null);
  // Alle fokussierbaren Zellelemente, ueber `cellKey()` adressiert -- fuer `focus()` nach einem
  // Pfeilschritt und fuer die Fokusrueckkehr nach dem Schliessen eines Editors.
  const cellRefs = useRef<Map<string, HTMLTableCellElement>>(new Map());
  // Vor dem Schliessen eines Editors PER TASTATUR (Enter/Escape, nicht `blur`) auf den
  // Zellschluessel gesetzt -- der Effekt darunter fokussiert die Zelle wieder, sobald `editing`
  // auf `null` faellt, und leert den Ref danach.
  const restoreFocusRef = useRef<string | null>(null);
  useEffect(() => {
    if (readOnly) return;
    setFocusCell((current) => {
      if (current === null) {
        return firstCell(grid);
      }
      const normalized = normalizeCell(current, grid);
      if (normalized && normalized.rowKey === current.rowKey && normalized.position === current.position) {
        return current;
      }
      return normalized;
    });
    // `grid` ist bei jedem Render ein neues Objekt; `gridKey` fasst seinen Inhalt zusammen, damit
    // der Effekt nur bei einer echten Aenderung laeuft (sonst wuerde jedes Tippen in einer Zelle
    // den Fokus-Zustand unnoetig neu bewerten).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridKey, readOnly]);

  useEffect(() => {
    if (editing === null && restoreFocusRef.current !== null) {
      const key = restoreFocusRef.current;
      restoreFocusRef.current = null;
      cellRefs.current.get(key)?.focus();
    }
  }, [editing]);

  function registerCellRef(key: string, element: HTMLTableCellElement | null) {
    if (element) {
      cellRefs.current.set(key, element);
    } else {
      cellRefs.current.delete(key);
    }
  }

  function focusCellElement(ref: MatrixCellRef) {
    const element = cellRefs.current.get(cellKey(ref));
    if (!element) return;
    element.focus();
    element.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    correctForStickyHeaderOverlap(element);
  }

  function handleCellFocus(ref: MatrixCellRef) {
    setFocusCell((current) =>
      current && current.rowKey === ref.rowKey && current.position === ref.position ? current : ref
    );
  }

  function handleCellKeyDown(
    event: KeyboardEvent<HTMLTableCellElement>,
    ref: MatrixCellRef,
    kind: 'value',
    fieldKey?: string
  ) {
    // Nur, wenn das Ereignis wirklich von der Zelle selbst kommt -- nicht aus dem ueberlagerten
    // Eingabefeld heraus (das hat sein eigenes `onKeyDown`, siehe Enter/Escape dort).
    if (event.target !== event.currentTarget) return;
    const { key } = event;
    if (key === 'ArrowUp' || key === 'ArrowDown' || key === 'ArrowLeft' || key === 'ArrowRight') {
      event.preventDefault();
      const next = nextCell(ref, key, grid);
      if (next.rowKey !== ref.rowKey || next.position !== ref.position) {
        setFocusCell(next);
        focusCellElement(next);
      }
      return;
    }
    if (key === 'Enter' || key === 'F2') {
      event.preventDefault();
      if (kind === 'value' && fieldKey !== undefined) {
        startEditValue(ref.position, fieldKey);
      }
    }
  }

  /** Zusaetzliche Attribute einer fokussierbaren Zelle -- leer im Protokoll (`readOnly`). */
  function gridCellProps(
    ref: MatrixCellRef,
    kind: 'value',
    fieldKey?: string
  ) {
    if (readOnly) return {};
    const key = cellKey(ref);
    const isFocused =
      focusCell !== null && focusCell.rowKey === ref.rowKey && focusCell.position === ref.position;
    return {
      role: 'gridcell' as const,
      tabIndex: isFocused ? 0 : -1,
      'data-cell': key,
      ref: (element: HTMLTableCellElement | null) => registerCellRef(key, element),
      onFocus: () => handleCellFocus(ref),
      onKeyDown: (event: KeyboardEvent<HTMLTableCellElement>) => handleCellKeyDown(event, ref, kind, fieldKey),
    };
  }

  function startEditValue(measurementIndex: number, fieldKey: string) {
    if (readOnly) return;
    const path = `measurements.${measurementIndex}.values.${fieldKey}`;
    const key = editKey({ kind: 'value', measurementIndex, fieldKey });
    snapshotRef.current = { key, cell: getPath(form.getValues(), path) as EmissionReportCellValue };
    setEditing({ kind: 'value', measurementIndex, fieldKey });
  }

  function commitValue(measurementIndex: number, fieldKey: string) {
    const path = `measurements.${measurementIndex}.values.${fieldKey}`;
    const key = editKey({ kind: 'value', measurementIndex, fieldKey });
    const current = getPath(form.getValues(), path) as EmissionReportCellValue;
    const previous = snapshotRef.current?.key === key ? snapshotRef.current.cell : current;
    const finalCell = applyCellEdit(previous, { value: current.value, belowLimit: current.belowLimit });
    form.setFieldValue(path, finalCell, NO_REMOUNT);
    snapshotRef.current = null;
    setEditing(null);
  }

  function cancelValue(measurementIndex: number, fieldKey: string) {
    const path = `measurements.${measurementIndex}.values.${fieldKey}`;
    const key = editKey({ kind: 'value', measurementIndex, fieldKey });
    if (snapshotRef.current?.key === key) {
      form.setFieldValue(path, snapshotRef.current.cell, NO_REMOUNT);
    }
    snapshotRef.current = null;
    setEditing(null);
  }

  /**
   * CR-02: Eine laufende Bearbeitung schliessen, BEVOR eine Messspalte ausgeblendet wird.
   *
   * Seit Punkt 4 (35.1-04) nummeriert `removeMeasurement` nicht mehr neu -- `editing` haelt
   * weiterhin denselben Feldpfad, auch nach dem Markieren einer ANDEREN Spalte als
   * ausgeschlossen. Wird aber genau die gerade bearbeitete Spalte ausgeschlossen, verschwindet
   * sie aus `visibleColumns` und damit das offene Eingabefeld ersatzlos; ohne dieses Schliessen
   * ginge eine noch nicht committete Eingabe verloren.
   *
   * Der Kommentar am Loeschknopf behauptete frueher, `onMouseDown -> preventDefault()` lasse
   * eine laufende Bearbeitung "ganz normal per blur committen". Es bewirkt das Gegenteil: es
   * verhindert genau dieses `blur`. Deshalb wird hier ausdruecklich committet -- der getippte
   * Wert bleibt erhalten und wird normalisiert, statt verworfen zu werden.
   */
  function closeOpenEdit() {
    if (editing === null) {
      return;
    }
    commitValue(editing.measurementIndex, editing.fieldKey);
  }

  return (
    <div
      className={classes.mxWrap}
      style={{ '--mx-label-width': `${MATRIX_LABEL_COLUMN_WIDTH_PX}px` } as CSSProperties}
    >
      <div className={classes.mxHead}>
        <div className={classes.mxHeadBar}>
          <h2 className={classes.mxHeadTitle}>Alle Werte</h2>
          {!readOnly && (
            <span className={classes.legend}>
              <span>
                <i className={classes.legendSwatch} style={{ background: 'var(--mantine-color-orange-6)' }} />
                von Hand geändert
              </span>
              <span>
                <i className={classes.legendSwatch} style={{ background: 'var(--mantine-color-vdzGray-3)' }} />
                leer
              </span>
            </span>
          )}
          {!readOnly && (
            <span className={classes.hint}>Zelle anklicken oder mit Pfeiltasten und Enter ändern</span>
          )}
        </div>
      </div>

      <table
        className={classes.mx}
        role={readOnly ? undefined : 'grid'}
        aria-label={readOnly ? undefined : 'Alle Werte'}
      >
        <thead>
          <tr>
            <th className={classes.fl}>Merkmal</th>
            {visibleColumns.map(({ measurement, position }) => (
              <th key={measurement.index} className={classes.mc}>
                {/* Abweichung von Sketch 020 (Entscheidung, Punkt 4): der Spaltenkopf behaelt die
                    URSPRUENGLICHE Nummer der Messung, statt die sichtbaren Spalten neu
                    durchzunummerieren -- Protokolleintraege des Mappers und der
                    Rueckgaengig-Text beziehen sich auf genau diese Nummer. */}
                <span className={classes.mn}>Messung {measurement.index}</span>
                <span className={classes.md}>
                  {measurement.date ? formatIsoDate(measurement.date) : measurement.dateRaw ?? '—'}
                </span>
                <span className={classes.md}>
                  {[measurement.operatingMode, measurement.time].filter(Boolean).join(' · ')}
                </span>
                {!readOnly && (
                  <button
                    type="button"
                    className={classes.delCol}
                    title="Diese Messung nicht anlegen"
                    // Dieselbe Fokus-Absicherung wie beim "=/<"-Umschalter (BelowLimitToggle):
                    // der Knopf reisst den Fokus nicht an sich, bevor React die Loeschung
                    // verarbeitet hat. Das unterdrueckt allerdings AUCH das `blur` der gerade
                    // offenen Zelle -- deren Bearbeitung wird deshalb hier ausdruecklich
                    // geschlossen, vor dem Markieren als ausgeschlossen (CR-02, siehe
                    // closeOpenEdit).
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => {
                      closeOpenEdit();
                      onDeleteMeasurement(position);
                    }}
                  >
                    löschen
                  </button>
                )}
              </th>
            ))}
            <th className={classes.un}>Einheit</th>
          </tr>
        </thead>
        <tbody>
          {rowGroups.map((group) => (
            <Fragment key={group.title}>
              <tr className={classes.gr}>
                <td colSpan={columnCount}>
                  <span className={classes.grLabel}>{group.title}</span>
                </td>
              </tr>
              {group.fields.map((field) => (
                <tr key={field.key}>
                  <td className={`${classes.fl} ${classes.cellLabel}`}>{field.label}</td>
                  {visibleColumns.map(({ measurement, position }) => {
                    const cell = measurement.values[field.key] ?? {
                      value: null,
                      belowLimit: false,
                      empty: null,
                    };
                    const active =
                      editing?.kind === 'value' &&
                      editing.measurementIndex === position &&
                      editing.fieldKey === field.key &&
                      !readOnly;
                    const changed = isChanged(sourceData, values, position, field.key);
                    const cellRef: MatrixCellRef = { rowKey: `value:${field.key}`, position };
                    return (
                      <td
                        key={measurement.index}
                        title={active ? undefined : cellTitle(cell)}
                        className={[
                          classes.cell,
                          classes.mc,
                          !readOnly && classes.editable,
                          cell.value === null && !active && classes.empty,
                          changed && !active && classes.edited,
                        ]
                          .filter(Boolean)
                          .join(' ')}
                        // siehe Kommentar bei `valueBasis` oben.
                        onClick={() => {
                          if (!active) startEditValue(position, field.key);
                        }}
                        {...gridCellProps(cellRef, 'value', field.key)}
                      >
                        <span className={active ? classes.ghost : undefined}>{cellText(cell)}</span>
                        {active && (
                          <div className={classes.cellEditor}>
                            <ValidatedNumberInput
                              form={form}
                              field={`measurements.${position}.values.${field.key}.value`}
                              belowLimitField={`measurements.${position}.values.${field.key}.belowLimit`}
                              kind="decimal"
                              align="right"
                              size="xs"
                              autoFocus
                              {...EMISSION_INPUT_PROPS}
                              styles={{
                                root: { height: '100%' },
                                wrapper: { height: '100%' },
                                input: { height: '100%', minHeight: 0 },
                              }}
                              onBlur={() => commitValue(position, field.key)}
                              // NACH dem Preset gesetzt, damit unser eigener Handler gewinnt --
                              // siehe Kommentar bei `oxygenReference` oben.
                              onKeyDown={(event) => {
                                handleDecimalKey(event);
                                if (event.key === 'Enter') {
                                  event.preventDefault();
                                  restoreFocusRef.current = cellKey(cellRef);
                                  commitValue(position, field.key);
                                }
                                if (event.key === 'Escape') {
                                  event.preventDefault();
                                  restoreFocusRef.current = cellKey(cellRef);
                                  cancelValue(position, field.key);
                                }
                              }}
                            />
                          </div>
                        )}
                      </td>
                    );
                  })}
                  <td className={classes.un}>{field.unit}</td>
                </tr>
              ))}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}
